import { InputHourSerieType } from '../../../building-energy-model/hour-series-calculator'
import {
  calcNetCoolingDensityHourSerie,
  coolingCalculator,
} from '../../../energy-system/cooling-hour-series-calculator'
import { calcVentilationHourSeries } from '../../../energy-system/ventilation-hour-series-calculator'
import {
  BuildingEnergyModelFragment,
  EnergySystemType,
} from '../../../generated/graphql'
import { i18n } from '../../../i18n'
import { hoursInYear } from '../../../util/hours-in-year'
import { monthsSumSerie, yearHours, yearMonths } from './calibration-util'
import {
  createChartSerie,
  createChartSerieValues,
  CustomLineStyles,
  CustomSeries,
  defaultCustomSeries,
} from './chart-util'

const DEFAULT_COOLING_LINETYPE = CustomLineStyles.Solid
const DEFAULT_COOLING_LINEWIDTH = 2

export const calcNetCoolingByTempChartData = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): CustomSeries => {
  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.netCooling')

  const result: CustomSeries = defaultCustomSeries(id)

  const coolingDensitySerie = calcNetCoolingDensityHourSerie(
    model,
    outsideTempHourSerie,
    model.area,
    year,
  )

  result.data = createChartSerieValues(
    outsideTempHourSerie,
    coolingDensitySerie,
  )

  return result
}

export const calcSortedCoolingDensityInYearByHourChartData = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): CustomSeries => {
  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.netCooling')

  let coolingDensitySerie = calcNetCoolingDensityHourSerie(
    model,
    outsideTempHourSerie,
    model.area,
    year,
  )

  coolingDensitySerie = coolingDensitySerie.sort((a, b) => b - a)

  return createChartSerie(
    id,
    yearHours(),
    coolingDensitySerie,
    CustomLineStyles.Solid,
    4,
  )
}

export const calcSumCoolingInYearByMonthChartData = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): CustomSeries[] => {
  const cooling = model.energySystems.find(
    (s) => s.type === EnergySystemType.Cooling,
  )

  if (!cooling) return []

  const { area } = model

  const ventilationResult = calcVentilationHourSeries(
    model,
    outsideTempHourSerie,
    year,
  )

  const ahuCoolingDemandSerie =
    ventilationResult.inputs.find(
      (r) => r.inputSerieType === InputHourSerieType.VentilationCoolingDemand,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const coolingHourSeries = coolingCalculator(
    outsideTempHourSerie,
    ahuCoolingDemandSerie,
    cooling,
    area,
    year,
  )

  const serieIds = [
    i18n.t('buildingEnergyModels.calibration.dataTypes.systemCooling'),
    i18n.t('buildingEnergyModels.calibration.dataTypes.spaceCooling'),
    i18n.t('buildingEnergyModels.calibration.dataTypes.ahuCooling'),
  ]

  for (let i = 0; i < coolingHourSeries[0].hourSerie.length; i++) {
    for (let j = 0; j < serieIds.length; j++) {
      coolingHourSeries[j].hourSerie[i] /= area
    }
  }

  const monthValues = yearMonths()

  const result: CustomSeries[] = []

  for (let i = 0; i < serieIds.length; i++) {
    result.push(
      createChartSerie(
        serieIds[i],
        monthValues,
        monthsSumSerie(coolingHourSeries[i].hourSerie, year),
        DEFAULT_COOLING_LINETYPE,
        DEFAULT_COOLING_LINEWIDTH,
        true,
      ),
    )
  }

  return result
}
